import { useMutation } from '@apollo/client';
import { FormProvider } from 'react-hook-form';
import { toast } from 'react-toastify';
import { z } from 'zod';
import { graphql } from '~/apollo/generated/v3';
import type {
  CompanyPartsFragment,
  StudyAccessInput,
} from '~/apollo/generated/v3/graphql';
import { Button } from '~/components/ui/button';
import { HookFormErrors } from '~/components/ui/forms/HookFormErrors';
import { Select } from '~/components/ui/forms/select';
import type { StudyAccessParent } from '~/components/upload/study/study-access-manager';
import { useZodForm } from '~/utils/forms';
import type { RefetchQueries } from '~/utils/graphql';
import { ppNumberOrNull } from '~/utils/zod';

export const CREATE_STUDY_ACCESS = graphql(`
  mutation CreateStudyAccess($studyAccess: StudyAccessInput!) {
    createStudyAccess(studyAccess: $studyAccess) {
      id
    }
  }
`);

export function CreateStudyAccessForm({
  parentType,
  parentId,
  availableCompanies,
  refetchQueries,
}: {
  parentType: StudyAccessParent;
  parentId: number;
  availableCompanies: Pick<CompanyPartsFragment, 'id' | 'name'>[];
  refetchQueries: RefetchQueries;
}) {
  const [createStudyAccess, { loading, error }] = useMutation(
    CREATE_STUDY_ACCESS,
    { refetchQueries },
  );

  const initialValues = () => ({ companyId: '' });

  const form = useZodForm({
    schema: z.object({ companyId: z.preprocess(ppNumberOrNull, z.number()) }),
    values: initialValues(),
  });

  const handleSubmit = form.handleSubmit(async values => {
    // Input should have either projectId OR studyId set but not both.
    const saInput: StudyAccessInput = {
      companyId: values.companyId,
      projectId: parentType === 'project' ? parentId : null,
      studyId: parentType === 'study' ? parentId : null,
    };

    try {
      await createStudyAccess({
        variables: {
          studyAccess: saInput,
        },
      });
      toast.success('Study access granted successfully.');
      form.reset(initialValues());
    } catch (err) {
      toast.error('There was a problem creating the study access.');
    }
  });

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Select
          {...form.register('companyId')}
          label="Grant access to:"
          options={availableCompanies.map(c => ({
            value: c.id.toString(),
            label: c.name,
          }))}
          required
        />

        <HookFormErrors graphQLError={error} />

        <div className="text-center">
          <Button type="submit" color="primary" loading={loading}>
            Grant Access
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}
